import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, ArrowLeft, CreditCard, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import airportImage from '../../assets/airportt.png';
import useWalletData from '../../hooks/useWalletData';
import firebaseFunctionsService from '../../services/firebaseFunctionsService';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { db } from '../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth } from '../../config/firebase';

const quickAmounts = [25, 50, 100, 200, 500, 1000];
const STRIPE_SUPPORTED_COUNTRIES = ['CA'];

const requiredEnv = key => {
  const value = import.meta.env?.[key];
  if (!value) {
    console.warn(`Missing ${key}. Stripe işlemleri devre dışı kalacak.`);
  }
  return value;
};

const STRIPE_PUBLISHABLE_KEY = requiredEnv('VITE_STRIPE_PUBLISHABLE_KEY');

const formatCurrency = value => {
  if (!value) return '';
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '';
  const formatted = new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(numValue));
  const sign = numValue < 0 ? '-' : '';
  return `${sign}CAD ${formatted}`;
};

export default function AddMoneyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, initializing } = usePublicAuth();
  const { balance } = useWalletData(!!user);
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState(null);
  const [saveCard, setSaveCard] = useState(false);
  const [nameOnCard, setNameOnCard] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('CA');
  const [postalCode, setPostalCode] = useState('');

  // Update form fields when user changes
  useEffect(() => {
    if (user) {
      setNameOnCard(user?.displayName || user?.email?.split('@')[0] || '');
      setEmail(user?.email || '');
    }
  }, [user]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [cardReady, setCardReady] = useState(false);
  const [stripeUnavailable, setStripeUnavailable] = useState(false);
  const [isCardPanelOpen, setIsCardPanelOpen] = useState(true);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loadingCards, setLoadingCards] = useState(false);

  const cardNumberContainerRef = useRef(null);
  const cardExpiryContainerRef = useRef(null);
  const cardCvcContainerRef = useRef(null);
  const stripeRef = useRef(null);
  const stripeElementsRef = useRef(null);
  const cardNumberElementRef = useRef(null);
  const cardExpiryElementRef = useRef(null);
  const cardCvcElementRef = useRef(null);
  const waitIntervalRef = useRef(null);
  const waitTimeoutRef = useRef(null);
  const [cardCompletion, setCardCompletion] = useState({
    number: false,
    expiry: false,
    cvc: false,
  });

  useEffect(() => {
    setCardReady(
      cardCompletion.number && cardCompletion.expiry && cardCompletion.cvc,
    );
  }, [cardCompletion]);

  // Load saved cards
  const loadSavedCards = useCallback(async () => {
    if (!user?.uid) {
      setSavedCards([]);
      return;
    }

    console.log('🔄 Loading saved cards...', {
      hasCustomerId: !!user?.stripeCustomerId,
      customerId: user?.stripeCustomerId,
      userId: user?.uid,
    });

    // Try to get customer ID from user object first, then Firestore
    let customerId = user?.stripeCustomerId;

    if (!customerId && user?.uid) {
      console.log('🔍 Customer ID not in user object, checking Firestore...');
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          customerId = userData?.stripeCustomerId;
          console.log('💾 Customer ID from Firestore:', customerId);
        }
      } catch (error) {
        console.error('❌ Error fetching customer ID from Firestore:', error);
      }
    }

    if (!customerId) {
      console.log('⚠️ No Stripe Customer ID found');
      setSavedCards([]);
      return;
    }

    setLoadingCards(true);
    try {
      const authToken = await auth.currentUser?.getIdToken();
      const result = await firebaseFunctionsService.getSavedPaymentMethods(
        customerId,
        user.uid,
        authToken,
      );

      console.log('💳 Saved cards result:', {
        success: result.success,
        paymentMethodsCount: result.paymentMethods?.length || 0,
        paymentMethods: result.paymentMethods,
      });

      if (result.success && result.paymentMethods) {
        // Filter cards to ensure they belong to the current customer
        // Backend should already filter, but double-check here
        const validCards = result.paymentMethods.filter(card => {
          // If card has customer property, verify it matches
          if (card.customer && card.customer !== customerId) {
            console.warn('⚠️ Card belongs to different customer:', card.id);
            return false;
          }
          return true;
        });

        setSavedCards(validCards);
        console.log(
          `✅ Loaded ${validCards.length} saved card(s) for customer ${customerId}`,
        );

        // If selected card is no longer valid, clear selection
        if (
          selectedCard &&
          !validCards.some(card => card.id === selectedCard.id)
        ) {
          console.warn('⚠️ Selected card no longer valid, clearing selection');
          setSelectedCard(null);
        }
      } else {
        console.log('⚠️ No saved cards found or error:', result.error);
        setSavedCards([]);
        setSelectedCard(null);
      }
    } catch (error) {
      console.error('❌ Error loading saved cards:', error);
      setSavedCards([]);
    } finally {
      setLoadingCards(false);
    }
  }, [user?.uid, user?.stripeCustomerId]);

  // Load saved cards when user is available
  useEffect(() => {
    if (user?.uid) {
      loadSavedCards();
    }
  }, [user?.uid, loadSavedCards]);

  // Delete saved card
  const handleDeleteCard = async (cardId, cardLast4) => {
    if (!window.confirm(t('wallet.deleteCardConfirm', { last4: cardLast4 }))) {
      return;
    }

    try {
      const customerId = user?.stripeCustomerId;
      if (!customerId) {
        setStatus({
          type: 'error',
          message: t('wallet.customerIdNotFound'),
        });
        return;
      }

      const authToken = await auth.currentUser?.getIdToken();
      const result = await firebaseFunctionsService.deletePaymentMethod(
        cardId,
        customerId,
        user.uid,
        authToken,
      );

      if (result.success) {
        // Remove card from local state
        setSavedCards(prevCards =>
          prevCards.filter(card => card.id !== cardId),
        );
        // Clear selection if deleted card was selected
        if (selectedCard?.id === cardId) {
          setSelectedCard(null);
        }
        setStatus({
          type: 'success',
          message: t('wallet.cardDeletedSuccessfully'),
        });
      } else {
        setStatus({
          type: 'error',
          message: result.error || t('wallet.failedToDeleteCard'),
        });
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      setStatus({
        type: 'error',
        message: error.message || t('wallet.failedToDeleteCard'),
      });
    }
  };

  // Handle Add New Card button
  const handleAddNewCard = () => {
    setSelectedCard(null);
    setIsCardPanelOpen(true);
  };

  useEffect(() => {
    const cleanup = () => {
      if (cardNumberElementRef.current) {
        cardNumberElementRef.current.destroy();
        cardNumberElementRef.current = null;
      }
      if (cardExpiryElementRef.current) {
        cardExpiryElementRef.current.destroy();
        cardExpiryElementRef.current = null;
      }
      if (cardCvcElementRef.current) {
        cardCvcElementRef.current.destroy();
        cardCvcElementRef.current = null;
      }
      stripeElementsRef.current = null;
      stripeRef.current = null;
      if (waitIntervalRef.current) {
        clearInterval(waitIntervalRef.current);
        waitIntervalRef.current = null;
      }
      if (waitTimeoutRef.current) {
        clearTimeout(waitTimeoutRef.current);
        waitTimeoutRef.current = null;
      }
    };

    const mountCardElement = () => {
      if (
        stripeRef.current ||
        !cardNumberContainerRef.current ||
        !cardExpiryContainerRef.current ||
        !cardCvcContainerRef.current ||
        !window.Stripe ||
        !STRIPE_PUBLISHABLE_KEY
      ) {
        return;
      }

      try {
        stripeRef.current = window.Stripe(STRIPE_PUBLISHABLE_KEY);
        stripeElementsRef.current = stripeRef.current.elements({
          appearance: { theme: 'flat', variables: { colorPrimary: '#FACC15' } },
        });
        const baseStyle = {
          style: {
            base: {
              fontFamily: 'inherit',
              fontSize: '15px',
              color: '#111827',
              '::placeholder': { color: '#9CA3AF' },
            },
            invalid: {
              color: '#EF4444',
            },
          },
        };
        const cardNumber = stripeElementsRef.current.create(
          'cardNumber',
          baseStyle,
        );
        const cardExpiry = stripeElementsRef.current.create(
          'cardExpiry',
          baseStyle,
        );
        const cardCvc = stripeElementsRef.current.create('cardCvc', baseStyle);

        cardNumber.mount(cardNumberContainerRef.current);
        cardExpiry.mount(cardExpiryContainerRef.current);
        cardCvc.mount(cardCvcContainerRef.current);

        cardNumberElementRef.current = cardNumber;
        cardExpiryElementRef.current = cardExpiry;
        cardCvcElementRef.current = cardCvc;

        const handleChange = (field, event) => {
          setCardError(event.error?.message || null);
          setCardCompletion(prev => ({
            ...prev,
            [field]: event.complete,
          }));
        };

        cardNumber.on('change', event => handleChange('number', event));
        cardExpiry.on('change', event => handleChange('expiry', event));
        cardCvc.on('change', event => handleChange('cvc', event));
      } catch (error) {
        console.error('Stripe initialization failed:', error);
        setStripeUnavailable(true);
      }
    };

    if (window.Stripe) {
      mountCardElement();
    } else {
      waitIntervalRef.current = setInterval(() => {
        if (window.Stripe) {
          mountCardElement();
          if (waitIntervalRef.current) {
            clearInterval(waitIntervalRef.current);
            waitIntervalRef.current = null;
          }
        }
      }, 250);

      waitTimeoutRef.current = setTimeout(() => {
        if (!stripeRef.current) {
          setStripeUnavailable(true);
          if (waitIntervalRef.current) {
            clearInterval(waitIntervalRef.current);
            waitIntervalRef.current = null;
          }
        }
      }, 8000);
    }

    return cleanup;
  }, []);

  const handleSelectAmount = value => {
    setAmount(value.toString());
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const numericAmount = Number(amount);

    if (!numericAmount || numericAmount < 10) {
      setStatus({
        type: 'error',
        message: t('wallet.pleaseEnterAtLeast'),
      });
      return;
    }

    if (numericAmount > 10000) {
      setStatus({
        type: 'error',
        message: t('wallet.maximumDepositAmount'),
      });
      return;
    }

    if (!user?.uid) {
      setStatus({
        type: 'error',
        message: t('wallet.mustBeLoggedIn'),
      });
      return;
    }

    setIsProcessing(true);
    setStatus(null);

    try {
      console.log('💰 Starting payment via Firebase Functions...', {
        amount: numericAmount,
        userId: user.uid,
        email: user.email,
      });

      // Check if user's country is supported for Stripe payments
      let userCountry = user?.country || user?.countryCode || null;

      // Fallback to Firestore only if not in user object
      if (!userCountry) {
        try {
          const userDoc = await Promise.race([
            getDoc(doc(db, 'users', user.uid)),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout')), 3000),
            ),
          ]);
          const userData = userDoc.data();
          userCountry = userData?.country || userData?.countryCode || null;
        } catch (fetchError) {
          console.warn('Failed to fetch country from Firestore:', fetchError);
        }
      }

      // If country is not set, default to 'CA' (Canada) and save to Firestore
      if (!userCountry) {
        console.log('⚠️ Country not found, setting default to CA');
        userCountry = 'CA';
        try {
          await setDoc(
            doc(db, 'users', user.uid),
            {
              country: 'CA',
              countryCode: 'CA',
            },
            { merge: true },
          );
          console.log('✅ Default country CA set in Firestore');
        } catch (updateError) {
          console.warn('Failed to set default country:', updateError);
        }
      }

      if (!STRIPE_SUPPORTED_COUNTRIES.includes(userCountry.toUpperCase())) {
        setStatus({
          type: 'error',
          message: t('wallet.stripeNotAvailableInCountry', {
            country: userCountry,
          }),
        });
        setIsProcessing(false);
        return;
      }

      // First, ensure customer exists - check Firestore for latest customer ID
      let customerId = user.stripeCustomerId;

      // Always check Firestore to ensure we have the latest customer ID
      if (user?.uid) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData?.stripeCustomerId) {
              customerId = userData.stripeCustomerId;
              console.log('💾 Customer ID from Firestore:', customerId);
            }
          }
        } catch (error) {
          console.error('❌ Error fetching customer ID from Firestore:', error);
        }
      }

      if (!customerId) {
        console.log('👤 Creating Stripe customer...');
        const customerResult = await firebaseFunctionsService.createCustomer(
          user.email,
          user.displayName || user.email?.split('@')[0] || 'User',
          user.uid,
        );

        if (customerResult.success) {
          customerId = customerResult.customerId;
          console.log('✅ Customer created:', customerId);

          // Update Firestore with new customer ID
          try {
            await setDoc(
              doc(db, 'users', user.uid),
              { stripeCustomerId: customerId },
              { merge: true },
            );
          } catch (error) {
            console.error('❌ Error updating customer ID in Firestore:', error);
          }
        } else {
          throw new Error('Failed to create customer');
        }
      }

      // If saved card is selected, use it
      if (selectedCard?.id) {
        console.log(
          '💳 Using saved card:',
          selectedCard.id,
          'with customer:',
          customerId,
        );

        // Verify that the saved card belongs to this customer
        // If saved card was loaded with a different customer ID, it might cause issues
        // Reload saved cards to ensure they match current customer
        const authToken = await auth.currentUser?.getIdToken();
        const cardsCheck =
          await firebaseFunctionsService.getSavedPaymentMethods(
            customerId,
            user.uid,
            authToken,
          );

        const cardExists =
          cardsCheck.success &&
          cardsCheck.paymentMethods?.some(card => card.id === selectedCard.id);

        if (!cardExists) {
          setStatus({
            type: 'error',
            message: t('wallet.selectedCardNotAssociated'),
          });
          setIsProcessing(false);
          setSelectedCard(null);
          await loadSavedCards();
          return;
        }

        const intent = await firebaseFunctionsService.createPaymentIntent(
          numericAmount,
          'cad',
          {
            firebase_uid: user.uid,
            origin: 'web',
            type: 'wallet_deposit',
            email: email || user.email,
          },
          customerId,
        );

        const clientSecret =
          intent?.clientSecret ||
          intent?.client_secret ||
          intent?.client_secret_id;
        const paymentIntentId =
          intent?.paymentIntentId ||
          intent?.paymentIntent?.id ||
          intent?.id ||
          clientSecret?.split('_secret')[0];

        if (!clientSecret) {
          throw new Error(t('wallet.stripeClientSecretMissing'));
        }

        // Confirm with saved payment method
        const confirmation = await stripeRef.current.confirmCardPayment(
          clientSecret,
          {
            payment_method: selectedCard.id,
          },
        );

        if (confirmation.error) {
          throw new Error(confirmation.error.message);
        }

        if (!paymentIntentId) {
          throw new Error(t('wallet.paymentReferenceMissing'));
        }

        await firebaseFunctionsService.confirmPaymentIntent(
          paymentIntentId,
          user.uid,
          numericAmount,
        );

        // Reload saved cards in case a new card was saved
        await loadSavedCards();

        setStatus({
          type: 'success',
          message: t('wallet.depositSuccessful'),
        });
        setAmount('');
        setIsProcessing(false);
        return;
      }

      // If no saved card selected, use card elements
      if (!stripeRef.current || !cardNumberElementRef.current) {
        setStatus({
          type: 'error',
          message: t('wallet.secureCardEntryNotReady'),
        });
        setIsProcessing(false);
        return;
      }

      const intent = await firebaseFunctionsService.createPaymentIntent(
        numericAmount,
        'cad',
        {
          firebase_uid: user.uid,
          origin: 'web',
          type: 'wallet_deposit',
          email: user.email,
        },
        customerId,
      );

      const clientSecret =
        intent?.clientSecret ||
        intent?.client_secret ||
        intent?.client_secret_id;
      const paymentIntentId =
        intent?.paymentIntentId ||
        intent?.paymentIntent?.id ||
        intent?.id ||
        clientSecret?.split('_secret')[0];

      if (!clientSecret) {
        throw new Error(t('wallet.stripeClientSecretMissing'));
      }

      const confirmation = await stripeRef.current.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardNumberElementRef.current,
            billing_details: {
              name:
                nameOnCard ||
                user?.displayName ||
                user?.email?.split('@')[0] ||
                'User',
              email: email || user?.email,
              address: {
                country: country,
                postal_code: postalCode || undefined,
              },
            },
          },
          setup_future_usage: saveCard ? 'off_session' : undefined,
        },
      );

      if (confirmation.error) {
        throw new Error(confirmation.error.message);
      }

      if (!paymentIntentId) {
        throw new Error(t('wallet.paymentReferenceMissing'));
      }

      await firebaseFunctionsService.confirmPaymentIntent(
        paymentIntentId,
        user.uid,
        numericAmount,
      );

      // Wait a bit for Stripe to save the card (if user chose to save it)
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Reload saved cards in case a new card was saved
      await loadSavedCards();

      if (cardNumberElementRef.current) {
        cardNumberElementRef.current.clear();
      }
      if (cardExpiryElementRef.current) {
        cardExpiryElementRef.current.clear();
      }
      if (cardCvcElementRef.current) {
        cardCvcElementRef.current.clear();
      }

      setCardCompletion({ number: false, expiry: false, cvc: false });
      setCardError(null);
      setStatus({
        type: 'success',
        message: t('wallet.depositSuccessful'),
      });
      setAmount('');
    } catch (error) {
      console.error('💥 Payment Error Details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
      setStatus({
        type: 'error',
        message: error.message || t('wallet.paymentFailed'),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!user && !initializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-4xl px-4 pt-32 pb-16 text-center">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-xl border border-gray-100 dark:border-slate-800">
            <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-4">
              {t('wallet.addMoney')}
            </h1>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('wallet.pleaseSignIn')}
            </p>
            <button
              onClick={() => navigate('/auth/login')}
              className="inline-flex items-center justify-center rounded-2xl bg-black dark:bg-slate-700 px-6 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800"
            >
              {t('common.goToLogin')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[420px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <button
            onClick={() => navigate('/wallet')}
            className="inline-flex items-center gap-2 text-white/80 text-sm font-semibold mb-6 hover:text-white transition"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')} {t('wallet.toWallet')}
          </button>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.4em] text-yellow-200">
                {t('wallet.depositFunds')}
              </p>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('wallet.addBalance')}
              </h1>
              <p className="text-xl text-yellow-50 mb-6 max-w-2xl">
                {t('wallet.fundWalletDescription')}
              </p>
              <div className="rounded-2xl bg-white/10 border border-white/20 p-4 text-white">
                <p className="text-sm uppercase tracking-[0.2em] text-yellow-200 mb-1">
                  {t('wallet.currentBalance')}
                </p>
                <p className="text-3xl font-black">{formatCurrency(balance)}</p>
              </div>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <img
                src={airportImage}
                alt="Add Money"
                className="w-full max-w-md h-auto rounded-2xl shadow-2xl border border-white/20"
              />
            </div>
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        <form
          onSubmit={handleSubmit}
          className="rounded-3xl bg-white dark:bg-slate-900 shadow-xl border border-gray-100 dark:border-slate-800 p-8 space-y-8"
        >
          <div>
            <p className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-widest">
              {t('wallet.chooseAmount')}
            </p>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {quickAmounts.map(value => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleSelectAmount(value)}
                  className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                    Number(amount) === value
                      ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400'
                      : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 hover:border-yellow-300 dark:hover:border-yellow-500'
                  }`}
                >
                  {formatCurrency(value)}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300">
              {t('wallet.customAmount')}
            </label>
            <div className="flex items-center gap-3 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-4 py-3 focus-within:ring-2 focus-within:ring-yellow-300 dark:focus-within:ring-yellow-500 focus-within:bg-white dark:focus-within:bg-slate-700">
              <DollarSign className="h-5 w-5 text-gray-400 dark:text-slate-500" />
              <input
                type="number"
                min="10"
                step="1"
                placeholder="100"
                value={amount}
                onChange={event => setAmount(event.target.value)}
                className="w-full bg-transparent text-lg font-semibold text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none"
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {t('wallet.minimumDeposit')}
            </p>
          </div>

          {/* Saved Cards */}
          {(user?.stripeCustomerId || user?.uid) && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300">
                {t('wallet.savedCards')}
              </label>
              {loadingCards ? (
                <div className="flex items-center justify-center py-4">
                  <div className="text-sm text-gray-500 dark:text-slate-400">
                    {t('wallet.loadingCards')}
                  </div>
                </div>
              ) : savedCards.length > 0 ? (
                <div className="space-y-2">
                  {savedCards.map(card => (
                    <div
                      key={card.id}
                      className="flex items-center gap-3 rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedCard(
                            selectedCard?.id === card.id ? null : card,
                          )
                        }
                        className={`flex-1 flex items-center gap-3 ${
                          selectedCard?.id === card.id
                            ? 'text-gray-900 dark:text-white'
                            : 'text-gray-600 dark:text-slate-400'
                        }`}
                      >
                        <CreditCard
                          className={`h-5 w-5 ${
                            selectedCard?.id === card.id
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-400 dark:text-slate-500'
                          }`}
                        />
                        <div className="flex-1 text-left">
                          <div className="text-sm font-semibold">
                            {card.card?.brand?.toUpperCase() || 'Card'}
                          </div>
                          <div className="text-xs">
                            •••• {card.card?.last4 || '****'}
                          </div>
                        </div>
                        {selectedCard?.id === card.id && (
                          <div className="h-5 w-5 rounded-full bg-black dark:bg-yellow-500 flex items-center justify-center">
                            <span className="text-white dark:text-black text-xs font-bold">
                              ✓
                            </span>
                          </div>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleDeleteCard(card.id, card.card?.last4 || '****')
                        }
                        className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition"
                      >
                        <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddNewCard}
                    className="w-full rounded-2xl border-2 border-dashed border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 px-4 py-3 text-sm font-semibold text-gray-700 dark:text-slate-300 hover:border-yellow-400 dark:hover:border-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition"
                  >
                    {t('wallet.addNewCard')}
                  </button>
                </div>
              ) : (
                <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-4 py-6 text-center">
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    {t('wallet.noSavedCards')}
                  </p>
                </div>
              )}
            </div>
          )}

          {!selectedCard && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300">
                {t('wallet.cardDetails')}
              </label>
              <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-800 dark:text-white">
                    {cardReady
                      ? t('wallet.cardDetailsReady')
                      : t('wallet.enterCardInfo')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {cardReady
                      ? t('wallet.readyToCharge')
                      : t('wallet.openSecurePanel')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsCardPanelOpen(true)}
                  className="rounded-xl border border-gray-200 dark:border-slate-700 px-3 py-2 text-xs font-semibold text-gray-900 dark:text-white hover:border-yellow-300 dark:hover:border-yellow-500 hover:text-yellow-500 transition"
                >
                  {cardReady ? t('wallet.editCard') : t('wallet.enterCard')}
                </button>
              </div>
              {cardError && (
                <p className="text-sm text-rose-500 dark:text-rose-400 font-semibold">
                  {cardError}
                </p>
              )}
              {stripeUnavailable && (
                <p className="text-sm text-rose-500 dark:text-rose-400 font-semibold">
                  {t('wallet.unableToLoadStripe')}
                </p>
              )}
            </div>
          )}

          {status && (
            <div
              className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
                status.type === 'error'
                  ? 'border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400'
                  : 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
              }`}
            >
              {status.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isProcessing || (!cardReady && !selectedCard) || !amount}
            className="w-full rounded-2xl bg-black dark:bg-slate-700 px-6 py-4 text-base font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isProcessing
              ? t('wallet.processing')
              : t('wallet.addAmount', {
                  amount: formatCurrency(Number(amount) || 0) || 'CAD 0.00',
                })}
          </button>
          <p className="text-xs text-gray-500 dark:text-slate-400 text-center">
            {t('wallet.poweredByStripe')}
          </p>
        </form>
      </main>
      {/* Card drawer */}
      <div
        className={`fixed inset-0 z-50 transition ${
          isCardPanelOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
            isCardPanelOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsCardPanelOpen(false)}
        ></div>
        <div
          className={`absolute top-0 right-0 h-full w-full sm:w-[420px] bg-white dark:bg-slate-900 shadow-2xl transition-transform duration-300 flex flex-col ${
            isCardPanelOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-black text-gray-900 dark:text-white">
                {t('wallet.addCard')}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setIsCardPanelOpen(false)}
              className="text-sm font-semibold text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
            >
              {t('wallet.close')}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <p className="text-sm text-gray-600 dark:text-slate-400">
              {t('wallet.cardDataEncrypted')}
            </p>

            {/* Card Information Section */}
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  {t('wallet.cardInformation')}
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                      {t('wallet.nameOnCard')}
                    </label>
                    <input
                      type="text"
                      value={nameOnCard}
                      onChange={e => setNameOnCard(e.target.value)}
                      placeholder="John Doe"
                      className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 dark:focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                      {t('wallet.cardNumber')}
                    </label>
                    <div
                      className="rounded-xl border border-gray-200 dark:border-slate-700 px-4 py-2.5 bg-white dark:bg-slate-800"
                      ref={cardNumberContainerRef}
                    ></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                        {t('wallet.mmYy')}
                      </label>
                      <div
                        className="rounded-xl border border-gray-200 dark:border-slate-700 px-4 py-2.5 bg-white dark:bg-slate-800"
                        ref={cardExpiryContainerRef}
                      ></div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                        {t('wallet.cvc')}
                      </label>
                      <div
                        className="rounded-xl border border-gray-200 dark:border-slate-700 px-4 py-2.5 bg-white dark:bg-slate-800"
                        ref={cardCvcContainerRef}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Address Section */}
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  {t('wallet.billingAddress')}
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                      {t('wallet.email')}
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 dark:focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                      {t('wallet.countryOrRegion')}
                    </label>
                    <select
                      value={country}
                      onChange={e => setCountry(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 dark:focus:ring-yellow-500"
                    >
                      <option value="CA">Canada</option>
                      <option value="US">United States</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                      {t('wallet.postalCode')}
                    </label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={e => setPostalCode(e.target.value)}
                      placeholder="A1A 1A1"
                      className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 dark:focus:ring-yellow-500"
                    />
                  </div>
                </div>
              </div>
            </div>
            {cardError && (
              <p className="text-sm text-rose-500 dark:text-rose-400 font-semibold">
                {cardError}
              </p>
            )}
            {stripeUnavailable && (
              <p className="text-sm text-rose-500 dark:text-rose-400 font-semibold">
                {t('wallet.unableToLoadStripe')}
              </p>
            )}
            {/* Save Card Option */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="saveCard"
                checked={saveCard}
                onChange={e => setSaveCard(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-yellow-400 focus:ring-yellow-400 dark:focus:ring-yellow-500 dark:bg-slate-800"
              />
              <label
                htmlFor="saveCard"
                className="text-sm text-gray-700 dark:text-slate-300 cursor-pointer"
              >
                {t('wallet.savePaymentDetails')}
              </label>
            </div>
          </div>
          <div className="p-6 border-t border-gray-100 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setIsCardPanelOpen(false)}
              className="w-full rounded-2xl bg-black dark:bg-slate-700 text-yellow-400 font-semibold py-3 hover:bg-gray-900 dark:hover:bg-slate-800 transition"
            >
              {t('wallet.done')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
